//#region src/js/components/quick-order.js
/**
* Custom HTML element for handling bulk product orders.
* Manages multiple product quantity inputs, calculates totals, and submits orders via AJAX.
* 
* @class BulkForm
* @extends {HTMLElement}
* 
* @property {NodeList} inputs - Collection of number input elements for product quantities
* @property {HTMLElement} totalPrice - Element displaying the total price of all items
* @property {HTMLButtonElement} submit - Submit button for the form
* @property {HTMLElement} qtySpan - Element displaying the total quantity of all items
* 
* @example
* // HTML usage
* <product-bulk-order-form>
*   <input type="number" data-id="123" data-price="10.00" data-max="100" data-management="shopify">
*   <span class="bulk-qty-total"></span>
*   <span class="bulk-order-total"></span>
*   <button type="submit">Add to Cart</button>
* </product-bulk-order-form>
*/
/**
* Creates an instance of BulkForm and initializes event listeners.
* Sets up input event listeners for real-time total updates and click handler for form submission.
* 
* @constructor
*/
/**
* Submits the bulk order form by collecting all items with quantities greater than 0
* and adding them to the cart using the Liquid AJAX Cart API.
* 
* @method submitForm
* @returns {void}
*/
/**
* Updates the total price and quantity displays based on current input values.
* Enforces maximum quantity limits for items with Shopify inventory management.
* 
* @method updateTotals
* @returns {void}
* 
* @description
* - Iterates through all quantity inputs
* - Validates against maximum stock levels (data-max attribute)
* - Calculates total price using item prices (data-price attribute)
* - Sums total quantity across all items
* - Updates DOM elements with formatted totals
*/
var BulkForm = class extends HTMLElement {
	constructor() {
		super();
		this.inputs = this.querySelectorAll("input[type='number']");
		this.totalPrice = this.querySelector(".bulk-order-total");
		this.submit = this.querySelector("button[type='submit']");
		this.qtySpan = this.querySelector(".bulk-qty-total");
		this.inputs.forEach((input) => {
			input.addEventListener("input", () => {
				this.updateTotals();
			});
		});
		this.submit.addEventListener("click", this.submitForm.bind(this));
	}
	submitForm() {
		const formData = { items: [] };
		const options = {
			firstCallback: function() {},
			lastCallback: function() {},
			info: {}
		};
		this.inputs.forEach((input) => {
			if (input.value > 0) formData.items.push({
				id: input.dataset.id,
				quantity: parseFloat(input.value)
			});
		});
		const body = { items: formData.items };
		window.liquidAjaxCart.add(body, options);
	}
	updateTotals() {
		let total = 0;
		let qty = 0;
		this.inputs.forEach((input) => {
			if (input.value) {
				const max = parseFloat(input.dataset.max) || 1e3;
				const management = input.dataset.management;
				if (parseFloat(input.value) > max && management === "shopify") input.value = max;
				total += parseFloat(input.value) * parseFloat(input.dataset.price);
				qty += parseFloat(input.value);
			}
		});
		this.totalPrice.textContent = `${window.Moonbase.currency}${total.toFixed(2)}`;
		this.qtySpan.textContent = qty;
	}
};
customElements.define("product-bulk-order-form", BulkForm);
//#endregion
